import React, { useState, useEffect } from "react"; 
import { Card, Typography, Button } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { DocumentIcon } from "@heroicons/react/24/outline";

function CCCSessions() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFeatures, setTotalFeatures] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const resultsPerPage = 20;

  // Récupération des identifiants depuis le fichier .env
  const apiUsername = process.env.REACT_APP_API_USERNAME;
  const apiPassword = process.env.REACT_APP_API_PASSWORD;
  const auth = { username: apiUsername, password: apiPassword };

  // Base URL for constructing pagination URLs
  const baseUrl = "https://api.osiris.brussels/geoserver/ogc/features/v1/collections/cdco%3ACDCO_SEANCES/items";

  useEffect(() => {
    fetchSessions(1);
  }, []);

  const buildUrl = (page) => {
    const startIndex = (page - 1) * resultsPerPage;
    return `${baseUrl}?limit=${resultsPerPage}&sortby=-SEANCE_DATE&filter=LIB_OBJECT_FR+%3C%3E+%27Pr%C3%A9paration%27${startIndex > 0 ? `&startIndex=${startIndex}` : ''}`;
  };

  const fetchSessions = async (page) => {
    try {
      setLoading(true);
      
      const url = buildUrl(page);
      
      const response = await axios.get(url, { 
        auth,
        // Add these headers to help with CORS issues
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Mise à jour des sessions
      const newSessions = response.data.features || [];
      setSessions(newSessions);
      
      // Mise à jour du nombre total d'éléments
      if (response.data.numberMatched) {
        const total = parseInt(response.data.numberMatched, 10);
        setTotalFeatures(total);
        setTotalPages(Math.ceil(total / resultsPerPage));
      }
      
      setCurrentPage(page);
    } catch (error) {
      console.error(t("api-request-error-message"), ":", error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionTypeByLanguage = (session) =>
    currentLang === "nl" ? session.properties.LIB_OBJECT_NL : session.properties.LIB_OBJECT_FR;

  const getSessionTitleByLanguage = (session) =>
    currentLang === "nl" ? session.properties.SEANCE_TITLE_NL : session.properties.SEANCE_TITLE_FR;
  

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchSessions(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchSessions(currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    if (currentPage !== 1) {
      fetchSessions(1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-BE');
  };

  const openDocument = (docId) => {
    if (docId) {
      window.open(`https://osiris.brussels/Modules/Common/Pages/DownloadDocument.aspx?IdDoc=${docId}`, '_blank');
    }
  };

  // Fonction pour afficher les documents
  const renderDocumentButtons = (session) => {
    return (
      <div className="flex flex-row md:justify-start gap-2 items-center ml-8 ">
        {session.properties.ID_DOC_AGENDA && (
          <button 
            onClick={() => openDocument(session.properties.ID_DOC_AGENDA)}
            className="text-blue-600 hover:text-blue-800 transition-colors flex flex-col items-center"
            title={t('ccc-sessions-tab-document-agenda')}
          >
            <DocumentIcon className="w-5 h-5" />
            <span className="text-xs text-center">{t('ccc-sessions-tab-document-agenda')}</span>
          </button>
        )}
        {session.properties.ID_DOC_PV && (
          <button 
            onClick={() => openDocument(session.properties.ID_DOC_PV)}
            className="text-blue-600 hover:text-blue-800 transition-colors flex flex-col items-center"
            title={t('ccc-sessions-tab-document-pv')}
          >
            <DocumentIcon className="w-5 h-5" />
            <span className="text-xs text-center">{t('ccc-sessions-tab-document-pv')}</span>
          </button>
        )}
        {session.properties.ID_DOC_ANNEX && (
          <button 
            onClick={() => openDocument(session.properties.ID_DOC_ANNEX)}
            className="text-blue-600 hover:text-blue-800 transition-colors flex flex-col items-center"
            title={t('ccc-sessions-tab-document-appendix')}
          >
            <DocumentIcon className="w-5 h-5" />
            <span className="text-xs text-center">{t('ccc-sessions-tab-document-appendix')}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6 m-2 sm:m-8">
      <div className="text-2xl font-bold text-blue-800 dark:text-yellow-800">
        {t('ccc-sessions-main-title')}
      </div>

      <Card className="w-full overflow-hidden">
        {/* Tableau pour écrans moyens et grands */}
        <div className="hidden md:block">
          <table className="w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t('ccc-sessions-tab-header-column-session')}
                  </Typography>
                </th>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t('ccc-sessions-tab-header-column-date')}
                  </Typography>
                </th>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t('ccc-sessions-tab-header-column-type')}
                  </Typography>
                </th>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t('ccc-sessions-tab-header-column-title')}
                  </Typography>
                </th>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3 text-center flex md:justify-start pl-8">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t('ccc-sessions-tab-header-column-documents')}
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center p-3">{t('data-loading-message')}</td>
                </tr>
              ) : sessions.length > 0 ? (
                sessions.map((session, index) => (
                  <tr key={session.id || index} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50 transition-colors">
                    <td className="p-3">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {session.properties.SEANCE_NR}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {formatDate(session.properties.SEANCE_DATE)}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {getSessionTypeByLanguage(session)}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {getSessionTitleByLanguage(session)}
                      </Typography>
                    </td>
                    <td className="p-3">
                      {renderDocumentButtons(session)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center p-3">{t('data-not-available-message')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Affichage carte pour mobile */}
        <div className="md:hidden">
          {loading ? (
            <div className="text-center p-4">{t('data-loading-message')}</div>
          ) : sessions.length > 0 ? (
            <div className="flex flex-col gap-4 p-4">
              {sessions.map((session, index) => (
                <Card key={session.id || index} className="p-4 shadow-md">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <Typography variant="h6" color="blue-gray" className="font-medium">
                        {session.properties.SEANCE_NR}
                      </Typography>
                      <Typography variant="small" color="blue-gray" className="font-normal">
                        {formatDate(session.properties.SEANCE_DATE)}
                      </Typography>
                    </div>
                    
                    <Typography variant="small" color="blue-gray" className="font-semibold">
                      {getSessionTypeByLanguage(session)}
                    </Typography>
                    
                    <Typography variant="small" color="blue-gray" className="font-normal mt-1">
                      {getSessionTitleByLanguage(session)}
                    </Typography>
                    
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <Typography variant="small" color="blue-gray" className="font-medium">
                        {t('ccc-sessions-tab-header-column-documents')}:
                      </Typography>
                      {renderDocumentButtons(session)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-4">{t('data-not-available-message')}</div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-blue-gray-50 p-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="text" 
              size="sm"
              className="p-3 flex items-center justify-center"
              onClick={goToFirstPage} 
              disabled={currentPage === 1 || loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
              </svg>
            </Button>
            <Button 
              variant="text" 
              size="sm"
              className="p-3 flex items-center justify-center"
              onClick={goToPreviousPage} 
              disabled={currentPage === 1 || loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </Button>
            
            <div className="flex items-center gap-3">
              <span className="text-blue-gray-600">{currentPage}</span>
              <span className="text-blue-gray-600">/ {totalPages}</span>
            </div>
            
            <Button 
              variant="text" 
              size="sm"
              className="p-3 flex items-center justify-center"
              onClick={goToNextPage} 
              disabled={currentPage >= totalPages || loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default CCCSessions;